import React, { useEffect, useRef, forwardRef, useCallback, useMemo, useState } from 'react';
import { OrderedSet, List, OrderedMap, Map } from 'immutable';
import flow from 'lodash/fp/flow';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import Api from '@/domain/api';
import {
  documentsGridGetRowsAction,
  documentsGridDocumentsByPageIndexAction,
  documentsGridApplyTransactionAction,
  documentsGridSearchAfterByPageIndexAction,
} from '@/domain/documents/documentsActions';
import {
  gridApplyTransactionSelector,
  documentsListListSelector,
  documentCountSelector,
  getGridSearchAfterByPageIndexSelector,
} from '@/domain/documents/documentSelector';
import { gridPresetGridStateReportedAction } from '@/domain/documents/gridPreset/gridPresetActions';
import {
  gridPresetCurrentSelector,
  gridPresetRevisionSelector,
} from '@/domain/documents/gridPreset/gridPresetSelectors';
import { IGNORED_ON_APPLY, fromGridState, isLayoutStateKey, toGridState } from '@/domain/documents/gridPreset/convert';
import { agGridRefreshServerSideSelector } from '@/domain/agGrid/selectors';
import { agGridRefreshServerSideAction } from '@/domain/agGrid/actions';
import { GridApplyTransactionFactory } from '@/domain/documents/factories';
import { isCompanyConfigurationLoadedSelector } from '@/domain/companies/companiesSelector';
import {
  changeWidthIfMasterViewNotAvailable,
  setExelModeFilter,
  addColDefaultParams,
  suppressSelectAll,
  isServiceColumn,
} from '@/components/AgGrid/helpers';
import {
  addSelectionMasterDetailToFirstColumn,
  addColumnsVisibilityColumnDef,
  addContextMenuColumnDef,
  addPreviewColumnDef,
  addLinkedButtonColumnRef,
} from '@/components/AgGrid/Documents/serviceColumnDefs';
import { getWorkspaceSelectionColumnDef, placeSelectionColumn } from '@/components/AgGrid/Documents/selectionColumn';
import { toSavedColumnLayout } from '@/domain/documents/savedColumnLayout';
import { CHECKBOX_ROW_SELECTION } from '@/components/AgGrid/helpers/rowSelection';
import { leadingEdge, trailingEdge } from '@/components/AgGrid/helpers/gridEdges';
import * as ACL from '@/domain/restriction';
import { addFilters } from '@/components/AgGrid/helpers/filterHelpers';
import { toRequestSortModel } from '@/components/AgGrid/helpers/sortModel';
import { documentsGridRowNodesAdapter, gridDocumentsListAdapter } from '@/domain/documents/adapters';
import { getNestedCategory } from '@/domain/categories/helpers';
import { GRID_PAGE_SIZE } from '@/domain/documents/constants';
import { PAGINATION_PANELS } from '@/components/AgGrid/helpers/paginationPanels';
import { createOverlayComponentSelector, emptyOverlays } from '@/components/AgGrid/helpers/overlays';
import { getWorkspaceGridPerPage, setWorkspaceGridPerPage } from '@/domain/documents/helpers';

// components
import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  GridStateModule,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  CustomFilterModule,
  ColumnApiModule,
  CellApiModule,
} from 'ag-grid-community';
import {
  CellSelectionModule,
  ContextMenuModule,
  GroupFilterModule,
  MasterDetailModule,
  MultiFilterModule,
  ServerSideRowModelModule,
  SetFilterModule,
  StatusBarModule,
  ClipboardModule,
} from 'ag-grid-enterprise';
import Box from '@mui/material/Box';
import DocumentPreviewNavigation from '@/pages/company/WorkspaceRoute/components/DocumentPreviewNavigation';
import MasterViewCellRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/MasterViewCellRenderer';
import PreviewCellRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/PreviewCellRenderer';
import ContextCellRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/ContextCellRenderer';
import ApprovalsCellRenderer from '@/components/AgGrid/components/ApprovalsCellRenderer';
import TagsCellRenderer from '@/components/AgGrid/components/TagsCellRenderer';
import AlertCellRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/AlertCellRenderer';
import AgGridColumnHeader from '@/components/AgGrid/components/ColumnHeader';
import AmountSumStatusPanelRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/AmountSumStatusPanelRenderer';
import AmountCellRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/AmountCellRenderer';
import CustomSetColumnFilter from '@/components/AgGrid/components/CustomHeaderFilter/CustomSetColumnFilter/CustomSetColumnFilter';
import LinkedCellRenderer from '@/pages/company/WorkspaceRoute/components/Grid/components/LinkedCellRenderer';
import StubWhenEmpty from '@/pages/company/WorkspaceRoute/components/stub/StubWhenEmpty';

import usePropagateGridApi from '@/pages/company/WorkspaceRoute/components/Grid/usePropagateGridApiRef';
import {
  useWorkspaceGridPreset,
  searchWithoutSharedPreset,
} from '@/pages/company/WorkspaceRoute/components/Grid/useWorkspaceGridPreset';
import usePrevious from '@/hooks/usePrevious';
import useDidUpdateEffect from '@/hooks/useDidUpdateEffect';
import useScrollManager, { getWorkspaceScrollPosition } from '@/lib/scrollManager';
import useAgGridDocumentsProps from '@/components/AgGrid/Documents/useAgGridDocumentsProps';
import useColumnTypes from '@/components/AgGrid/Documents/useAgGridColumnTypes';
import useAgGridTranslations from '@/components/AgGrid/hooks/useAgGridTranslations';
import useAgGridRowClassesRules from '@/components/AgGrid/Documents/useAgGridRowClassesRules';
import useAgGridRtlMethods from '@/components/AgGrid/hooks/useAgGridRtlMethods';
import useGridContext from '@/pages/company/WorkspaceRoute/components/Grid/useGridContext';
import useFetchGridColumnDefs from '@/pages/company/WorkspaceRoute/components/Grid/useFetchGridColumnDefs';
import useRangeSelectionChanged from '@/pages/company/WorkspaceRoute/components/Grid/useRangeSelectionChanged';
import useAgGridDocumentsPreviewNavigation from '@/components/AgGrid/Documents/useAgGridDocumentsPreviewNavigation';
import useAgGridTheme from '@/components/AgGrid/hooks/useAgGridTheme';
import { filterMapping } from '@/components/AgGrid/Documents/filterHelpers';
// https://stackoverflow.com/questions/62202886/styles-injection-order-with-jss-and-material-ui-differs-between-dev-and-prod
// The main point I've missed it that <styles> are injected when makeStyles is called: injection order is the same than calls to makeStyles order.
import AgGridGlobalStyles from '@/components/AgGrid/components/AgGridGlobalStyles';
import AgGridDocumentsStyles from '@/components/AgGrid/Documents/AgGridDocumentsStyles';

import AgDateFilter from '@/components/AgGrid/components/AgDateFilter/AgDateFilterBE';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  MasterDetailModule,
  CellSelectionModule,
  StatusBarModule,
  ContextMenuModule,
  GridStateModule,
  PaginationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  CustomFilterModule,
  CellSelectionModule,
  ColumnApiModule,
  CellApiModule,
  ClipboardModule,
]);

export const PREVIEW_CONTEXT_NAME = 'gridList';

const overlayComponentSelector = createOverlayComponentSelector(emptyOverlays(StubWhenEmpty));

// Column changes arrive once per drag step while resizing; the grid reports them when the gesture settles.
// Filter and sort changes are reported at once — the chips and the document navigation read them.
const COLUMN_REPORT_DELAY = 250;

const Grid = forwardRef(
  (
    {
      onContextMenu,
      onChangeSelected,
      onDocumentOpen,
      selected,
      onPreview,
      onShowLinked,
      resetIsLinkedPanelSelected,
      isLinkedPanelSelected,
      preview,
      isTrashCategory,
    },
    ref,
  ) => {
    const dispatch = useDispatch();
    const routeParams = useParams();
    const { search } = useLocation();
    const agGridTheme = useAgGridTheme();
    const DEFAULT_PER_PAGE = useMemo(() => getWorkspaceGridPerPage(), []);

    const documentsList = useSelector(documentsListListSelector);
    const documentsCount = useSelector(documentCountSelector);
    const refreshDocumentNodes = useSelector(gridApplyTransactionSelector);
    const refreshServerSide = useSelector(agGridRefreshServerSideSelector);
    const isGranted = useSelector(ACL.isGranted);
    const gridSearchAfterByPageIndex = useSelector(getGridSearchAfterByPageIndexSelector);
    const isCompanyConfigurationLoaded = useSelector(isCompanyConfigurationLoadedSelector);
    const currentLayout = useSelector(gridPresetCurrentSelector);
    const layoutRevision = useSelector(gridPresetRevisionSelector);

    const agGridDocumentsProps = useAgGridDocumentsProps();
    const { getLocaleText } = useAgGridTranslations();
    const rowClassRules = useAgGridRowClassesRules();
    const { invertPinnedWhenRTL, enableRtl } = useAgGridRtlMethods();
    const columnTypes = useColumnTypes();
    const gridApiRef = useRef(null);
    // !!! Ref.current used to avoid unnecessary prop changes and re-renders in ag-grid instead of getting this data from Redux after changes
    // !!! dispatched data to redux mostly needed for propagate(sharing) to another components
    const onFirstDataRenderedRef = useRef(false);
    // The layout revision the grid shows: the one its initialState was built from, then the last one applied.
    const appliedRevisionRef = useRef(null);
    const columnReportRef = useRef({ timer: undefined, state: null });
    const previousSelectedSize = usePrevious(selected.size); // used for detect cmd/ctrl+A or escape
    const selectedIDsRef = useRef(selected);
    const searchAfterByPageIndexRef = useRef(Map()); // keys for navigation between pages, param used by b-end for pagination for huge list of documents(over 100k for example)
    // documentsOrderedMapRef used for pagination, dispatch actual documents list to redux
    // actual documents for current page
    const documentsOrderedMapRef = useRef(new OrderedMap()); // OrderedMap with adapted documents by ID
    const [isGridInit, setGridInit] = useState(false);
    const [isFirstDataFetched, setIsFirstDataFetched] = useState(false);
    const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
    const [initialState, setInitialState] = useState(null);

    const onCellSelectionChanged = useRangeSelectionChanged(gridApiRef);
    const category = getNestedCategory(routeParams);

    const hasMasterDetail = isGranted(ACL.IS_BOOKKEEPER_IN_FINANCIAL_COMPANY) && !isTrashCategory;

    const { rawColumnDefsIsBusy, rawColumnDefs } = useFetchGridColumnDefs();

    const defaultColumnDefs = useMemo(() => {
      if (rawColumnDefsIsBusy) return [];

      const columns = flow([
        addLinkedButtonColumnRef,
        addPreviewColumnDef,
        addSelectionMasterDetailToFirstColumn,
        addColumnsVisibilityColumnDef,
        addContextMenuColumnDef,
        addFilters(filterMapping),
        setExelModeFilter('windows'),
        addColDefaultParams,
      ])(rawColumnDefs);

      return changeWidthIfMasterViewNotAvailable(columns, hasMasterDetail);
    }, [rawColumnDefsIsBusy, rawColumnDefs, hasMasterDetail]);

    const columnDefs = useMemo(
      () => flow(suppressSelectAll, invertPinnedWhenRTL)(defaultColumnDefs),
      [defaultColumnDefs, invertPinnedWhenRTL],
    );

    // Default's columns, LTR, as the column definitions give them, in the saved shape: data columns only.
    const defaultLayoutColumns = useMemo(
      () =>
        toSavedColumnLayout(
          defaultColumnDefs.map(({ field, hide, pinned, width = 0, minWidth, maxWidth }) => ({
            colId: field,
            hide,
            pinned,
            width: Math.max(minWidth || 1, Math.min(maxWidth || 1000, width)),
          })),
        ),
      [defaultColumnDefs],
    );

    // The technical columns a layout leaves out, placed by their definitions (LTR) whenever a layout is applied.
    const technicalColumns = useMemo(
      () =>
        defaultColumnDefs
          .filter(({ field }) => isServiceColumn(field))
          .map(({ field, hide, pinned }) => ({ colId: field, hide: Boolean(hide), pinned: pinned || null })),
      [defaultColumnDefs],
    );

    const { isResolved: isLayoutResolved } = useWorkspaceGridPreset(defaultLayoutColumns);

    const selectionColumnDef = useMemo(() => getWorkspaceSelectionColumnDef(enableRtl), [enableRtl]);

    const canRenderGrid = isLayoutResolved && !!currentLayout && isCompanyConfigurationLoaded && !rawColumnDefsIsBusy;

    // DA-19691: AG Grid needs serverSideInitialRowCount > (targetRowIndex + visible rows) to restore
    // the saved page/scroll on mount (see AG Grid Server-Side "Initial Scroll Position"). Derive it
    // from the saved page instead of a network total: (pageIndex + 1) reaches the end of the saved
    // page, the extra + 1 is a full-viewport buffer. The exact count from the first getRows overwrites it.
    const serverSideInitialRowCount = useMemo(() => {
      const { pageIndex = 0 } = getWorkspaceScrollPosition();

      return (pageIndex + 2) * perPage;
    }, [perPage]);

    // A column change still waiting for its report belongs to the layout the grid showed before: an apply or a
    // company switch drops it, or it would overwrite the new layout.
    const cancelColumnReport = useCallback(() => {
      clearTimeout(columnReportRef.current.timer);
      columnReportRef.current = { timer: undefined, state: null };
    }, []);

    // The grid is not rendered while the layout resolves (company switch); the next one reports ready again.
    // AG Grid reads initialState once, when the grid is created: built from the working layout when the grid is
    // about to render, and again only after the layout is cleared, which also unmounts the grid.
    useEffect(() => {
      if (!isLayoutResolved) {
        cancelColumnReport();
        setGridInit(false);
        setInitialState(null);
        return;
      }
      if (!canRenderGrid || initialState) return;
      const { pageIndex = 0 } = getWorkspaceScrollPosition();
      appliedRevisionRef.current = layoutRevision;
      setInitialState({
        ...toGridState(currentLayout, { rtl: enableRtl, technicalColumns }),
        pagination: { page: pageIndex, pageSize: perPage },
        partialColumnState: true,
      });
    }, [
      isLayoutResolved,
      canRenderGrid,
      initialState,
      currentLayout,
      layoutRevision,
      enableRtl,
      technicalColumns,
      perPage,
      cancelColumnReport,
    ]);

    // An external layout change (preset, Reset, chips, shared link): one setState with the layout's keys only, so
    // pagination, scroll and selection stay. The grid reports it back with source 'api', which is not reported.
    useEffect(() => {
      const api = gridApiRef.current;
      if (!api || !isGridInit || !currentLayout || layoutRevision === appliedRevisionRef.current) return;
      appliedRevisionRef.current = layoutRevision;
      cancelColumnReport();
      api.setState(toGridState(currentLayout, { rtl: enableRtl, technicalColumns }), IGNORED_ON_APPLY);
      placeSelectionColumn(api);
    }, [isGridInit, currentLayout, layoutRevision, enableRtl, technicalColumns, cancelColumnReport]);

    useEffect(() => {
      if (gridApiRef.current && documentsOrderedMapRef.current.size > 0) {
        // this is for cmd/ctrl+A feature, listener is placed in BulkPanelComponent,
        // 'api' - send this source name for event, and skip handler for this
        // (the first parameter is the select-all mode, the event source is the second)
        // reset selected rows when we have empty array list
        if ((selected.size === 0 && previousSelectedSize) || isLinkedPanelSelected) {
          gridApiRef.current.deselectAll('all', 'api');
          selectedIDsRef.current = OrderedSet();
        } else if (selected.size === documentsOrderedMapRef.current.size && previousSelectedSize !== selected.size) {
          gridApiRef.current.selectAll('all', 'api');
          selectedIDsRef.current = OrderedSet(selected);
        }
      }
    }, [selected, previousSelectedSize, isLinkedPanelSelected]);

    // used for refresh data(silent mode - no request sent to backend) of grid after some changes for example moving to another category etc
    // dispatched in saga mostly
    useEffect(() => {
      const { nodes, transactionType } = refreshDocumentNodes;
      if (transactionType && nodes.size > 0 && gridApiRef.current) {
        const data = documentsGridRowNodesAdapter(refreshDocumentNodes.nodes);
        const transaction = {
          [refreshDocumentNodes.transactionType]: data,
        };

        const result = gridApiRef.current.applyServerSideTransaction(transaction);

        if (transactionType === 'remove' && result?.status === 'Applied') {
          documentsOrderedMapRef.current = documentsOrderedMapRef.current.filter((d) => !nodes.includes(d.documentID));
        }
        dispatch(documentsGridApplyTransactionAction(GridApplyTransactionFactory()));
      }
    }, [refreshDocumentNodes, dispatch]);

    // used for refresh data(hard mode - request sent to backend) of grid after some changes for example moving to another category etc
    useEffect(() => {
      if (typeof refreshServerSide === 'boolean') {
        gridApiRef.current?.deselectAll();
        gridApiRef.current?.refreshServerSide({ purge: refreshServerSide });
        dispatch(agGridRefreshServerSideAction(null));
      }
    }, [refreshServerSide, dispatch]);

    useEffect(() => {
      // when we come to document page from grid view and navigate between documents
      // we can return to the grid view, set Ref value from redux for navigate to correct page on grid
      if (!gridSearchAfterByPageIndex.equals(searchAfterByPageIndexRef.current)) {
        searchAfterByPageIndexRef.current = gridSearchAfterByPageIndex;
      }
    }, [gridSearchAfterByPageIndex]);

    // refresh grid data when category changed and skip it on first render
    useDidUpdateEffect(() => {
      gridApiRef.current?.deselectAll();
      gridApiRef.current?.refreshServerSide({ purge: true });
    }, [category]);

    // refresh grid data when [location search (URL query params)] changed and skip it on first render.
    // `?sharedPreset=` is not a search: the layout hook applies it and then removes it from the address.
    const searchKey = searchWithoutSharedPreset(search);
    useDidUpdateEffect(() => {
      gridApiRef.current?.refreshServerSide({ purge: false });
    }, [searchKey]);

    const getRowID = useCallback(({ data }) => data.documentID, []);

    const isRowMaster = useCallback((dataItem) => dataItem.hasMasterDetail, []);

    const onSelectionChanged = useCallback(
      ({ api, source }) => {
        // event source 'api' is set in useEffect
        if (!isLinkedPanelSelected || source !== 'api') {
          let selectedIDs;
          const { selectAll, toggledNodes } = api.getServerSideSelectionState();
          const documentIDs = documentsOrderedMapRef.current.keySeq().toOrderedSet();

          if (selectAll) {
            if (toggledNodes.length > 0) {
              selectedIDs = OrderedSet(documentIDs).subtract(toggledNodes);
            } else {
              selectedIDs = OrderedSet(documentIDs);
            }
          } else {
            selectedIDs = OrderedSet(toggledNodes);
          }

          if (!selectedIDs.equals(selectedIDsRef.current)) {
            selectedIDsRef.current = selectedIDs;
            resetIsLinkedPanelSelected();
            onChangeSelected(selectedIDs);
          }
        }
      },
      [onChangeSelected, resetIsLinkedPanelSelected, isLinkedPanelSelected],
    );

    const onRowGroupOpened = useCallback((e) => {
      if (e.expanded && gridApiRef.current) {
        gridApiRef.current.forEachNode((node) => {
          if (node.expanded && e.data.documentID !== node.data.documentID) {
            node.setExpanded(false);
          }
        });
      }
    }, []);

    const findColumnToUnpin = useCallback(
      (columnState, pinPosition) =>
        columnState.find((col) => !isServiceColumn(col.colId) && col.pinned === pinPosition),
      [],
    );

    const handleProcessUnpinnedColumns = useCallback(
      ({ api }) => {
        const columnState = api.getColumnState();

        let columnToUnpin = findColumnToUnpin(columnState, trailingEdge(enableRtl));

        if (!columnToUnpin) {
          columnToUnpin = findColumnToUnpin(columnState, leadingEdge(enableRtl));
        }

        // The state event reports the change.
        if (columnToUnpin) {
          api.setColumnsPinned([columnToUnpin.colId], null);
        }
      },
      [enableRtl, findColumnToUnpin],
    );

    const onClickOpen = useCallback(
      (_, doc) => {
        onDocumentOpen(doc.documentID);
      },
      [onDocumentOpen],
    );

    const frameworkComponents = useMemo(
      () => ({
        masterViewCellRenderer: MasterViewCellRenderer,
        approvalsCellRenderer: ApprovalsCellRenderer,
        tagsCellRenderer: TagsCellRenderer,
        amountCellRenderer: AmountCellRenderer,
        amountSumStatusPanel: AmountSumStatusPanelRenderer,
        previewCellRenderer: PreviewCellRenderer,
        linkedCellRenderer: LinkedCellRenderer,
        alertCellRenderer: AlertCellRenderer,
        contextCellRenderer: ContextCellRenderer,
        customSetColumnFilter: CustomSetColumnFilter,
        agColumnHeader: AgGridColumnHeader,
        customDateFilter: AgDateFilter,
      }),
      [],
    );

    const serverDatasource = useMemo(
      () => ({
        getRows: async (params) => {
          const {
            context: { fetchParams },
            success,
            fail,
            request,
            api,
          } = params;
          const currentPageIndex = api.paginationGetCurrentPage();
          // Filter handlers keep every column's model in the grid, so the request carries the applied filters even
          // for columns whose popup was never opened. The layout reaches the store through onStateUpdated.
          const { filterModel, sortModel, startRow, endRow } = request;
          const requestPageIndex = startRow / (endRow - startRow);

          const requestData = {
            filterModel,
            sortModel: toRequestSortModel(sortModel),
            startRow,
            endRow,
          };

          api.clearCellSelection();

          try {
            const { data } = await Api.getWorkspaceGridRows({
              data: {
                ...fetchParams,
                ...requestData,
                searchAfter:
                  currentPageIndex > 0
                    ? searchAfterByPageIndexRef.current.get((currentPageIndex - 1).toString())
                    : null,
              },
            });

            const { items, count, timeStamp, searchAfter } = data;

            const adaptedList = gridDocumentsListAdapter(items);
            const rowData = documentsGridRowNodesAdapter(adaptedList);
            const response = { rowData, rowCount: count };

            success(response);

            documentsOrderedMapRef.current = adaptedList;
            searchAfterByPageIndexRef.current = searchAfterByPageIndexRef.current.set(
              currentPageIndex.toString(),
              List(searchAfter),
            );

            dispatch({
              type: documentsGridGetRowsAction.success,
              payload: {
                list: adaptedList,
                count,
                timeStamp,
                count_without_link_panels: count,
              },
            });

            dispatch(
              documentsGridDocumentsByPageIndexAction({
                data: Map().set(requestPageIndex.toString(), adaptedList.keySeq().toList()),
                purge: true,
              }),
            );

            dispatch(
              documentsGridSearchAfterByPageIndexAction({
                purge: false,
                data: Map().set(currentPageIndex.toString(), List(searchAfter)),
              }),
            );

            setIsFirstDataFetched(true);
          } catch (error) {
            console.log('====== REQUEST FAILED', error);
            fail();
          }
        },
      }),
      [dispatch], // be careful when adding dependencies to hook - potentially invoked again every time values change
    );

    const onGridReady = useCallback(async (params) => {
      gridApiRef.current = params.api;
      placeSelectionColumn(params.api);
      setGridInit(true);
    }, []);

    const onFirstDataRendered = useCallback(() => {
      onFirstDataRenderedRef.current = true;
    }, []);

    const onPaginationChanged = useCallback(({ newPage, newPageSize, api }) => {
      if (newPageSize) {
        const size = api.paginationGetPageSize();

        api.deselectAll();
        setPerPage(size);
        setWorkspaceGridPerPage(size);
      }

      if (newPage) {
        api.deselectAll();
      }
    }, []);

    const onSortChanged = useCallback(
      ({ api, source }) => {
        const currentPageIndex = api.paginationGetCurrentPage();

        // for b-end pagination we must reset user to the first page when start manual sorting
        if (source === 'uiColumnSorted' && currentPageIndex > 0) {
          dispatch(
            documentsGridSearchAfterByPageIndexAction({
              purge: true,
              data: Map(),
            }),
          );

          searchAfterByPageIndexRef.current = Map();

          api.paginationGoToFirstPage();
        }
      },
      [dispatch],
    );

    const reportLayout = useCallback(
      (state) => dispatch(gridPresetGridStateReportedAction(fromGridState(state, { rtl: enableRtl }))),
      [dispatch, enableRtl],
    );

    const flushColumnReport = useCallback(() => {
      const pending = columnReportRef.current;
      cancelColumnReport();
      if (pending.state) reportLayout(pending.state);
    }, [reportLayout, cancelColumnReport]);

    // The grid's only way back to the store. Sources 'gridInitializing' and 'api' (initialState, setState) come
    // without a layout key, so neither is reported back; scroll, selection and focus are not the layout either.
    const onStateUpdated = useCallback(
      ({ state, sources }) => {
        if (!sources.some(isLayoutStateKey)) return;
        cancelColumnReport();
        if (sources.includes('filter') || sources.includes('sort')) {
          reportLayout(state);
          return;
        }
        columnReportRef.current = { state, timer: setTimeout(flushColumnReport, COLUMN_REPORT_DELAY) };
      },
      [reportLayout, flushColumnReport, cancelColumnReport],
    );

    // A column change made just before leaving (opening a document) is kept.
    useEffect(() => flushColumnReport, [flushColumnReport]);

    const { startIndex, handlePreviewNavigate, handleChangePreview, isPreviewOpen } =
      useAgGridDocumentsPreviewNavigation({
        gridApiRef,
        isGridReady: isGridInit,
        list: documentsList,
        previewContextKey: PREVIEW_CONTEXT_NAME,
        previewState: preview,
        onPreviewCallback: onPreview,
      });

    const componentsContext = useMemo(
      () => ({
        previewCellRenderer: {
          onClickPreview: handleChangePreview,
          onClickDocument: onDocumentOpen,
        },
        linkedCellRenderer: {
          onClickLinked: onShowLinked,
        },
        contextCellRenderer: {
          oncClickContextMenu: onContextMenu,
        },
      }),
      [handleChangePreview, onDocumentOpen, onShowLinked, onContextMenu],
    );

    const gridContext = useGridContext({
      components: componentsContext,
      autocompleteApiInstance: Api.getDocumentsGridFilterListByField,
    });

    usePropagateGridApi({ ref, gridApiRef, gridContext, selectedIDs: selected });

    useScrollManager({
      view: 'grid',
      isTargetLoaded: isFirstDataFetched,
      gridApi: gridApiRef.current,
    });

    // !IMPORTANT - be careful when changing next condition, we must wait for this data for grid to work correctly
    if (!canRenderGrid || !initialState) {
      return null;
    }

    return (
      <>
        <AgGridGlobalStyles />
        <AgGridDocumentsStyles />
        <Box className="ag-theme-dokka" display="flex" flexDirection="column" height="100%">
          <AgGridReact
            theme={agGridTheme}
            serverSideDatasource={serverDatasource}
            masterDetail={hasMasterDetail}
            isRowMaster={isRowMaster}
            components={frameworkComponents}
            detailRowHeight={400}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onStateUpdated={onStateUpdated}
            onRowGroupOpened={onRowGroupOpened}
            onSelectionChanged={onSelectionChanged}
            onCellSelectionChanged={onCellSelectionChanged}
            onPaginationChanged={onPaginationChanged}
            onSortChanged={onSortChanged}
            // props from useAgGridManualConfig hook
            enableRtl={enableRtl}
            getLocaleText={getLocaleText}
            rowClassRules={rowClassRules}
            // props from useAgGridDocumentsProps hook
            {...agGridDocumentsProps}
            rowSelection={CHECKBOX_ROW_SELECTION}
            selectionColumnDef={selectionColumnDef}
            rowModelType="serverSide"
            suppressServerSideFullWidthLoadingRow
            columnTypes={columnTypes}
            getRowId={getRowID}
            rowHeight={58}
            columnDefs={columnDefs}
            context={gridContext}
            cacheBlockSize={perPage}
            maxBlocksInCache={1}
            initialState={initialState}
            overlayComponentSelector={overlayComponentSelector}
            serverSideInitialRowCount={serverSideInitialRowCount} // saved-page estimate; exact count arrives from the first getRows
            pagination
            paginationPageSize={perPage}
            paginationPageSizeSelector={GRID_PAGE_SIZE}
            paginationPanels={PAGINATION_PANELS}
            processUnpinnedColumns={handleProcessUnpinnedColumns}
          />
        </Box>

        {isPreviewOpen && (
          <DocumentPreviewNavigation
            documentId={preview.documentId}
            setPreview={(document) => handleChangePreview(document?.documentID)}
            list={documentsList}
            allCount={documentsCount}
            onContextMenu={onContextMenu}
            onClick={onClickOpen}
            startIndex={startIndex}
            onNavigate={handlePreviewNavigate}
          />
        )}
      </>
    );
  },
);

const GridWithForwardedRef = ({ forwardedRef, ...props }) => <Grid ref={forwardedRef} {...props} />;

export default GridWithForwardedRef;
